import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useOrgClock } from '../../lib/useOrgClock';
import { PageRoot, PageHeader } from './Page';
import { ErrorState, OfflineState } from './QueryStates';
import { PageSkeleton } from './Skeleton';

type Icon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

/**
 * Hold a screen until the shop's timezone is known, then hand it over as a plain `string`.
 *
 * Every screen that prints a time or picks a day needs the org's zone, and six of them used
 * to paper over not having it yet with `?? 'America/New_York'` — see `useOrgClock` for what
 * that actually did to a Phoenix shop. Deleting the fallback is not enough on its own: the
 * zone is needed BEFORE the hooks run, in `useState(todayISO(tz))` initialisers and in the
 * arguments of the day query itself, so it cannot be resolved with a conditional halfway
 * down a component. Splitting the screen in two is what makes `tz: string` — not
 * `string | undefined` — available at the very top of the part that needs it.
 *
 * One idiom for all nine rather than a bespoke gate per page: this repo has already been
 * caught inventing a second way to express a rule six pages had agreed on (ticket 009).
 *
 * ⚠️ Branches FOUR ways, because the honesty law here has four states and the fourth is the
 * one that bites. A paused query is also pending, so gating on `isPending` alone shows a
 * skeleton that will never resolve to a tech with no signal and a cold cache — and gating
 * only on pending + error would turn a failed `org.info` into a permanent skeleton too.
 * Both are §7 shape 3, an error rendered as a loading state, which is the same class of lie
 * as the fallback this replaces. `queryState` orders the checks; do not reorder them.
 */
export function WithOrgClock({
  what,
  icon,
  title,
  children,
}: {
  /** Plain-English object name for the failure copy, e.g. "the schedule". */
  what: string;
  icon: Icon;
  /** Page title to keep on screen while erroring, so the shell doesn't vanish. */
  title: string;
  children: (tz: string) => ReactNode;
}) {
  const clock = useOrgClock();

  if (clock.state === 'loading') return <PageSkeleton />;

  if (clock.state !== 'ready' || !clock.tz) {
    return (
      <PageRoot>
        <PageHeader title={title} />
        {clock.state === 'offline' ? (
          <OfflineState icon={icon} what={what} />
        ) : (
          <ErrorState icon={icon} what={what} onRetry={clock.refetch} />
        )}
      </PageRoot>
    );
  }

  return <>{children(clock.tz)}</>;
}
